import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, ArrowLeft, Briefcase } from 'lucide-react';
import {
  getSuperAdminCompaniesApi,
  createSuperAdminCompanyApi,
  updateSuperAdminCompanyApi,
  deleteSuperAdminCompanyApi,
  getSuperAdminOrganizationsApi,
} from '../../api/api';
import { showMessage } from '../../context/MessageContext.jsx';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Pagination from '../../components/ui/Pagination.jsx';
import { usePagination } from '../../hooks/usePagination.js';
import './CompaniesList.css';

export default function CompaniesList() {
  const { organizationId } = useParams(); // Backend expects organizationId (organization's id, not tallyId)
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    companyId: '',
    companyName: '',
    companyEmail: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyCountry: '',
    companyContactNumber: '',
    companyGSTNumber: '',
    billWise: false,
    userWise: false,
    beatWise: false,
    approvalWise: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;
  const { totalPages, goToPage, goToNextPage, goToPreviousPage } = usePagination(
    totalCount,
    limit,
    currentPage,
    setCurrentPage
  );

  useEffect(() => {
    fetchOrganization();
    fetchCompanies();
  }, [organizationId, currentPage, search]);

  const fetchOrganization = async () => {
    try {
      const res = await getSuperAdminOrganizationsApi({ limit: 1000 });
      const orgs = res.data?.data || [];
      const org = orgs.find((o) => o.id === organizationId);
      if (!org) {
        showMessage('Organization not found', 'error');
        navigate('/superadmin/organizations');
        return;
      }
      setOrganization(org);
    } catch (err) {
      showMessage('Failed to load organization', 'error');
      navigate('/superadmin/organizations');
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: limit,
        ...(search && { search }),
      };
      const res = await getSuperAdminCompaniesApi(organizationId, params);
      setCompanies(res.data?.data || []);
      setTotalCount(res.data?.totalCount || 0);
    } catch (err) {
      showMessage('Failed to load companies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        companyId: company.companyId || '',
        companyName: company.companyName || '',
        companyEmail: company.companyEmail || '',
        companyAddress: company.companyAddress || '',
        companyCity: company.companyCity || '',
        companyState: company.companyState || '',
        companyCountry: company.companyCountry || '',
        companyContactNumber: company.companyContactNumber || '',
        companyGSTNumber: company.companyGSTNumber || '',
        billWise: company.billWise || false,
        userWise: company.userWise || false,
        beatWise: company.beatWise || false,
        approvalWise: company.approvalWise || false,
      });
    } else {
      setEditingCompany(null);
      setFormData({
        companyId: '',
        companyName: '',
        companyEmail: '',
        companyAddress: '',
        companyCity: '',
        companyState: '',
        companyCountry: '',
        companyContactNumber: '',
        companyGSTNumber: '',
        billWise: false,
        userWise: false,
        beatWise: false,
        approvalWise: false,
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCompany(null);
    setFormData({
      companyId: '',
      companyName: '',
      companyEmail: '',
      companyAddress: '',
      companyCity: '',
      companyState: '',
      companyCountry: '',
      companyContactNumber: '',
      companyGSTNumber: '',
      billWise: false,
      userWise: false,
      beatWise: false,
      approvalWise: false,
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});

    const errors = {};
    if (!formData.companyId.trim()) errors.companyId = 'Company ID is required';
    if (!formData.companyName.trim()) errors.companyName = 'Company Name is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      if (editingCompany) {
        await updateSuperAdminCompanyApi(organizationId, editingCompany.companyId, formData);
        showMessage('Company updated successfully', 'success');
      } else {
        await createSuperAdminCompanyApi(organizationId, formData);
        showMessage('Company created successfully', 'success');
      }
      handleCloseModal();
      fetchCompanies();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 'Operation failed';
      showMessage(errorMsg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`Are you sure you want to delete company "${company.companyName}"?`)) {
      return;
    }

    try {
      await deleteSuperAdminCompanyApi(organizationId, company.companyId);
      showMessage('Company deleted successfully', 'success');
      fetchCompanies();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 'Failed to delete company';
      showMessage(errorMsg, 'error');
    }
  };

  if (!organization) {
    return <div className="companies-list-loading">Loading organization...</div>;
  }

  return (
    <div className="companies-list">
      <div className="companies-list-header">
        <div className="companies-list-header-left">
          <Button
            variant="secondary"
            onClick={() => navigate('/superadmin/organizations')}
            className="companies-list-back-button"
          >
            <ArrowLeft size={18} />
            Back
          </Button>
          <div>
            <h1 className="companies-list-title">Companies</h1>
            <p className="companies-list-subtitle">
              Organization: {organization.ownerName} ({organization.tallyId})
            </p>
          </div>
        </div>
        <Button
          variant="primary"
          onClick={() => handleOpenModal()}
          className="companies-list-add-button"
        >
          <Plus size={18} />
          Add Company
        </Button>
      </div>

      <Card className="companies-list-filters">
        <div className="companies-list-search">
          <Search size={20} className="companies-list-search-icon" />
          <Input
            type="text"
            placeholder="Search companies..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="companies-list-search-input"
          />
        </div>
      </Card>

      {loading ? (
        <div className="companies-list-loading">
          <div className="spinner"></div>
          <p>Loading companies...</p>
        </div>
      ) : companies.length === 0 ? (
        <Card className="companies-list-empty">
          <Briefcase size={48} />
          <p>No companies found</p>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <Plus size={18} />
            Add First Company
          </Button>
        </Card>
      ) : (
        <>
          <div className="companies-list-table-container">
            <table className="companies-list-table">
              <thead>
                <tr>
                  <th>Company ID</th>
                  <th>Company Name</th>
                  <th>Email</th>
                  <th>Contact</th>
                  <th>City</th>
                  <th>Features</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.companyId}>
                    <td>{company.companyId}</td>
                    <td>{company.companyName}</td>
                    <td>{company.companyEmail || 'N/A'}</td>
                    <td>{company.companyContactNumber || 'N/A'}</td>
                    <td>{company.companyCity || 'N/A'}</td>
                    <td>
                      {company.billWise || company.userWise || company.beatWise || company.approvalWise ? (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {company.billWise && <span style={{ fontSize: '11px', padding: '2px 6px', background: '#e3f2fd', color: '#1976d2', borderRadius: '4px' }}>Bill Wise</span>}
                          {company.userWise && <span style={{ fontSize: '11px', padding: '2px 6px', background: '#e8f5e9', color: '#388e3c', borderRadius: '4px' }}>User Wise</span>}
                          {company.beatWise && <span style={{ fontSize: '11px', padding: '2px 6px', background: '#fff3e0', color: '#f57c00', borderRadius: '4px' }}>Beat Wise</span>}
                          {company.approvalWise && <span style={{ fontSize: '11px', padding: '2px 6px', background: '#fce4ec', color: '#c2185b', borderRadius: '4px' }}>Approval Wise</span>}
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>No features enabled</span>
                      )}
                    </td>
                    <td>
                      <div className="companies-list-actions">
                        <button
                          className="companies-list-action"
                          onClick={() => handleOpenModal(company)}
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="companies-list-action danger"
                          onClick={() => handleDelete(company)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
              onNextPage={goToNextPage}
              onPreviousPage={goToPreviousPage}
            />
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="companies-list-modal-overlay" onClick={handleCloseModal}>
          <div className="companies-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="companies-list-modal-header">
              <h2>{editingCompany ? 'Edit Company' : 'Add Company'}</h2>
              <button className="companies-list-modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="companies-list-modal-form">
              <FormField
                label="Company ID *"
                error={formErrors.companyId}
              >
                <Input
                  type="text"
                  value={formData.companyId}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                  placeholder="Enter Company ID"
                  disabled={!!editingCompany}
                  required
                />
              </FormField>

              <FormField
                label="Company Name *"
                error={formErrors.companyName}
              >
                <Input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  placeholder="Enter company name"
                  required
                />
              </FormField>

              <FormField
                label="Company Email"
                error={formErrors.companyEmail}
              >
                <Input
                  type="email"
                  value={formData.companyEmail}
                  onChange={(e) => setFormData({ ...formData, companyEmail: e.target.value })}
                  placeholder="Enter company email"
                />
              </FormField>

              <FormField
                label="Address"
                error={formErrors.companyAddress}
              >
                <Input
                  type="text"
                  value={formData.companyAddress}
                  onChange={(e) => setFormData({ ...formData, companyAddress: e.target.value })}
                  placeholder="Enter address"
                />
              </FormField>

              <div className="companies-list-modal-row">
                <FormField
                  label="City"
                  error={formErrors.companyCity}
                >
                  <Input
                    type="text"
                    value={formData.companyCity}
                    onChange={(e) => setFormData({ ...formData, companyCity: e.target.value })}
                    placeholder="Enter city"
                  />
                </FormField>

                <FormField
                  label="State"
                  error={formErrors.companyState}
                >
                  <Input
                    type="text"
                    value={formData.companyState}
                    onChange={(e) => setFormData({ ...formData, companyState: e.target.value })}
                    placeholder="Enter state"
                  />
                </FormField>
              </div>

              <div className="companies-list-modal-row">
                <FormField
                  label="Country"
                  error={formErrors.companyCountry}
                >
                  <Input
                    type="text"
                    value={formData.companyCountry}
                    onChange={(e) => setFormData({ ...formData, companyCountry: e.target.value })}
                    placeholder="Enter country"
                  />
                </FormField>

                <FormField
                  label="Contact Number"
                  error={formErrors.companyContactNumber}
                >
                  <Input
                    type="text"
                    value={formData.companyContactNumber}
                    onChange={(e) => setFormData({ ...formData, companyContactNumber: e.target.value })}
                    placeholder="Enter contact number"
                  />
                </FormField>
              </div>

              <FormField
                label="GST Number"
                error={formErrors.companyGSTNumber}
              >
                <Input
                  type="text"
                  value={formData.companyGSTNumber}
                  onChange={(e) => setFormData({ ...formData, companyGSTNumber: e.target.value })}
                  placeholder="Enter GST number"
                />
              </FormField>

              <div className="companies-list-modal-features" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>Feature Flags</h3>
                <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                  {[
                    { key: 'billWise', label: 'Bill Wise' },
                    { key: 'userWise', label: 'User Wise' },
                    { key: 'beatWise', label: 'Beat Wise' },
                    { key: 'approvalWise', label: 'Approval Wise' }
                  ].map(({ key, label }) => (
                    <label 
                      key={key}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        cursor: 'pointer', 
                        userSelect: 'none' 
                      }}
                    >
                      <div style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px' }}>
                        <input
                          type="checkbox"
                          checked={formData[key]}
                          onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                          style={{
                            opacity: 0,
                            width: 0,
                            height: 0,
                            position: 'absolute'
                          }}
                        />
                        <span style={{
                          position: 'absolute',
                          cursor: 'pointer',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: formData[key] ? '#4CAF50' : '#ccc',
                          transition: '0.3s',
                          borderRadius: '26px',
                        }}>
                          <span style={{
                            position: 'absolute',
                            height: '20px',
                            width: '20px',
                            left: formData[key] ? '26px' : '3px',
                            bottom: '3px',
                            backgroundColor: 'white',
                            transition: '0.3s',
                            borderRadius: '50%',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }} />
                        </span>
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="companies-list-modal-actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={submitting}
                >
                  {editingCompany ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

